'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import Main from './main';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { SpinnerGapIcon, LockKeyIcon } from '@phosphor-icons/react';
import { TimePilotLocaleProvider } from '@/i18n/TimePilotI18nProviders';
import { TimePilotLoadingScreen } from '@/i18n/TimePilotLoadingScreen';
import { useT } from '@/i18n/LocaleProvider';
import { FeatureFlagTypes } from '@/shared/types';
import { LD_INIT_TIMEOUT_SECONDS } from '@/lib/launch-darkly-init';
import { idleClock } from "@/lib/session/idle-clock";
import { redirectIfTabDisplaced } from "@/lib/session/tab-sync";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MarkdownEditorScrollbarGuard } from '@/components/markdown-editor-scrollbar-guard';
import { seedGatewaySession } from '@/lib/gateway-fetch';
import {
  AUTH_SESSION_REFRESHED_EVENT,
  AUTH_TENANT_LOCKED_EVENT,
  AUTH_WRONG_CELL_EVENT,
  refreshAuthSession,
} from '@/lib/refresh-auth-session';
import { msUntilRefresh, shouldRefreshNow } from '@/lib/session/refresh-schedule';

/** Retry delay on transient errors (5xx, network failure). */
const TRANSIENT_RETRY_MS = 30 * 1000;

/**
 * How often the maintenance (tenant-locked) screen re-checks /api/init for
 * recovery. The lockout-status probe stays reachable while locked, so once ops
 * unlocks, the next poll returns an authenticated payload and we reload into the
 * app. Kept short so recovery feels near-instant; at ≤35 users/tenant this is
 * trivial traffic. Focus/online listeners also trigger an immediate re-check.
 *
 * Overridable via NEXT_PUBLIC_TENANT_LOCKED_POLL_MS for local iteration (e.g.
 * point TR1900_LOCKOUT_STUB_URL at a local lockout stub to watch recovery
 * without waiting 30s). Falls back to 30s for any unset/invalid value, so prod
 * is unaffected.
 */
const TENANT_LOCKED_POLL_MS = (() => {
  const raw = Number(process.env.NEXT_PUBLIC_TENANT_LOCKED_POLL_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : 30 * 1000;
})();

type WrongCellState = Record<string, never>;

function MaintenanceScreen() {
  const t = useT();
  return (
    <div
      className='flex flex-col items-center justify-center h-screen gap-4 text-center px-6'
      aria-live='polite'
      role='status'
    >
      <SpinnerGapIcon size={42} className='animate-spin-only text-gray-400' aria-hidden='true' />
      <h1 className='text-2xl sm:text-3xl font-bold'>{t('maintenance.title')}</h1>
      <p className='text-gray-500 max-w-md'>{t('maintenance.body')}</p>
    </div>
  );
}

function AccessDeniedScreen() {
  const t = useT();
  return (
    <div className='flex flex-col items-center justify-center h-screen gap-4 text-center px-6'>
      <LockKeyIcon size={48} className='text-gray-400' />
      <h1 className='text-2xl sm:text-3xl font-bold'>{t('accessDenied.title')}</h1>
      <p className='text-gray-500 max-w-md'>{t('accessDenied.body')}</p>
      <a href='/api/sign-out' className='mt-2 text-sm text-gray-400 hover:text-gray-600 underline'>
        {t('accessDenied.signOut')}
      </a>
    </div>
  );
}

const Home: React.FC = () => {
  const [queryClient] = useState(() => new QueryClient());
  const [features, setFeatures] = useState<{ [key: string]: FeatureFlagTypes | null } | null>(null);
  const [appStartupReady, setAppStartupReady] = useState(false);
  const [wrongCell, setWrongCell] = useState<WrongCellState | null>(null);
  // True while this tenant is under a TR-1900 incident lockout. Terminal screen
  // with its own recovery poll (see the effect below) — NOT a redirect, so it
  // cannot loop the way a WRONG_CELL redirect could.
  const [tenantLocked, setTenantLocked] = useState(false);
  // Mirror of tenantLocked read inside fetchInit (a useCallback that must not
  // depend on it, or every lock/unlock would recreate the init loop).
  const tenantLockedRef = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest decoded access token, so the refocus/visibility backup can decide
  // whether a proactive refresh is due without re-reading React state.
  const decodedAccessTokenRef = useRef<Record<string, unknown> | null>(null);
  // True while a fetchInit() call is in flight — prevents the refocus handler
  // from stacking a second concurrent /api/init on top of the scheduled one.
  const refreshInFlightRef = useRef(false);
  // Indirection so the scheduled refresh timer (set from applyInitData) can call
  // the latest fetchInit without a useCallback dependency cycle.
  const fetchInitRef = useRef<() => void>(() => {});
  const handleAppStartupReady = useCallback(() => setAppStartupReady(true), []);

  /**
   * Adopt a fresh `/api/init` payload: push features (→ AuthContext tokens),
   * sync the idle-timeout activity, seed the gateway-session cache, and schedule
   * the next proactive refresh. Shared by the scheduled fetch and the on-demand
   * refresh fired from a cell-service 403 (AUTH_SESSION_REFRESHED_EVENT), so a
   * stale token recovered deep in the tree updates the whole app.
   */
  const applyInitData = useCallback((data: Record<string, unknown>) => {
    if (typeof data.lastActivityAt === 'number') {
      // The idle clock is a module singleton (lib/session/idle-clock), so this
      // reaches the IdleTimeoutManager mounted by the root layout without this
      // route having to own a SessionProvider (TR-3393). observeServerActivity
      // will not move the clock backwards, and cannot clear an expired latch.
      idleClock.observeServerActivity(data.lastActivityAt);
    }
    setFeatures(data as { [key: string]: FeatureFlagTypes | null });

    const rawAccessToken = (data.tokens as { rawAccessToken?: string } | undefined)?.rawAccessToken;
    if (rawAccessToken && data.cellId) {
      seedGatewaySession({
        accessToken: rawAccessToken,
        cellId: data.cellId as string,
        tenantId: (data.tenantId as string) || '',
      });
    }

    const decodedAccessToken = (data.tokens as Record<string, unknown>)?.decodedAccessToken as Record<string, unknown> | null;
    decodedAccessTokenRef.current = decodedAccessToken;
    const delay = msUntilRefresh(decodedAccessToken);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => fetchInitRef.current(), delay);
  }, []);

  /**
   * Single owner of the `WRONG_CELL` decision: redirect to the correct cell when
   * it's known and differs from the current origin (a same-origin redirect would
   * just reload-loop), otherwise show the in-app Access Denied screen. Shared by
   * the app-shell init loop and the on-demand recovery path (via
   * {@link AUTH_WRONG_CELL_EVENT}) so both behave identically.
   */
  const handleWrongCell = useCallback((correctCellUrl: string | null) => {
    if (correctCellUrl && correctCellUrl !== window.location.origin) {
      window.location.href = correctCellUrl;
    } else {
      setWrongCell({});
    }
  }, []);

  /**
   * Single owner of the TR-1900 lockout decision: show the terminal maintenance
   * screen and let the recovery-poll effect below drive re-check + reload on
   * unlock. Shared by the app-shell init loop and the on-demand recovery path
   * (via {@link AUTH_TENANT_LOCKED_EVENT}) so a lock discovered deep in the tree
   * after a cell 403 flips the shell immediately, instead of waiting for the
   * next proactive /api/init. Idempotent — safe to call when already locked.
   */
  const handleTenantLocked = useCallback(() => {
    tenantLockedRef.current = true;
    setTenantLocked(true);
  }, []);

  const fetchInit = useCallback(async () => {
    // Coalesce overlapping calls within the shell (scheduled timer vs. refocus
    // backup). The /api/init request itself is also coalesced inside
    // refreshAuthSession, so a shell refresh and an on-demand recovery refresh
    // share ONE round-trip and can never finish out of order.
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      // While the maintenance screen is up, poll in recovery mode: /api/init then
      // fails CLOSED on an unknown lockout probe (config-service blip), so a blip
      // keeps the screen up instead of returning an authenticated payload that
      // would reload us into the still-locked, all-403 shell. That makes every
      // recovery verdict below DEFINITIVE — a `refreshed`/`logout` means the
      // tenant is genuinely unlocked, never a fail-open false positive.
      const result = await refreshAuthSession({ lockoutRecovery: tenantLockedRef.current });
      if (result.kind === 'logout') {
        // Dead/unrefreshable session (or not authenticated). While locked this is
        // reached only after a genuine unlock (probe said `locked: false`) whose
        // real session is then idle/revoked/dead — so signing out is correct, not
        // a swallowed blip. refreshAuthSession picks /api/sign-out?scope=local vs
        // /api/auth/sign-in for us.
        window.location.href = result.href;
        return;
      }
      if (result.kind === 'wrong-cell') {
        handleWrongCell(result.correctCellUrl);
        return;
      }
      if (result.kind === 'tenant-locked') {
        // TR-1900 incident lockout. Show the terminal maintenance screen; the
        // recovery-poll effect below keeps re-checking and reloads on unlock.
        handleTenantLocked();
        return;
      }
      if (result.kind === 'refreshed' && tenantLockedRef.current) {
        // Recovered: the tenant was unlocked and /api/init now authenticates.
        // Reload so the full app shell mounts cleanly rather than trying to
        // resume from the maintenance screen's partial state.
        window.location.reload();
        return;
      }
      if (result.kind === 'unavailable') {
        // Transient (network/5xx) — retry shortly so a momentary blip doesn't
        // permanently kill the refresh loop.
        console.error('[page] Init unavailable (network/5xx) — retrying shortly.');
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => fetchInitRef.current(), TRANSIENT_RETRY_MS);
        return;
      }
      // kind === 'refreshed': refreshAuthSession already dispatched
      // AUTH_SESSION_REFRESHED_EVENT, which drives applyInitData below — a single
      // application point for shell- and recovery-initiated refreshes alike.
    } catch (err) {
      // refreshAuthSession is designed not to throw, but guard the loop anyway:
      // an unexpected rejection must still reschedule, or the proactive refresh
      // loop would silently die until a full page reload.
      console.error('[page] Init failed unexpectedly — retrying shortly.', err);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => fetchInitRef.current(), TRANSIENT_RETRY_MS);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [handleWrongCell, handleTenantLocked]);

  useEffect(() => {
    fetchInitRef.current = fetchInit;
  }, [fetchInit]);

  useEffect(() => {
    fetchInit();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [fetchInit]);

  // A stale access token rejected by a cell service (403) triggers an on-demand
  // /api/init refresh deep in the tree (see lib/refresh-auth-session). Adopt the
  // fresh payload here so AuthContext and every other cell-service consumer pick
  // up the new token, not just the call that recovered it.
  useEffect(() => {
    const onRefreshed = (e: Event) => {
      const detail = (e as CustomEvent).detail as Record<string, unknown> | undefined;
      // Only adopt payloads from the customer pool. An in-flight employee-pool
      // refresh that completes after the user navigates to / must not overwrite
      // the customer AuthContext with employee tokens.
      if (detail && detail.authenticated && detail.pool === 'customer') {
        if (redirectIfTabDisplaced('customer')) return;
        applyInitData(detail);
      }
    };
    window.addEventListener(AUTH_SESSION_REFRESHED_EVENT, onRefreshed);
    return () => window.removeEventListener(AUTH_SESSION_REFRESHED_EVENT, onRefreshed);
  }, [applyInitData]);

  // A cell-service call that hits a WRONG_CELL session during recovery defers the
  // decision here (see lib/cors-auth-recovery) so it matches the app-shell loop:
  // redirect to the correct cell or show Access Denied — never a sign-in bounce.
  useEffect(() => {
    const onWrongCell = (e: Event) => {
      const detail = (e as CustomEvent).detail as { correctCellUrl?: string | null } | undefined;
      handleWrongCell(detail?.correctCellUrl ?? null);
    };
    window.addEventListener(AUTH_WRONG_CELL_EVENT, onWrongCell);
    return () => window.removeEventListener(AUTH_WRONG_CELL_EVENT, onWrongCell);
  }, [handleWrongCell]);

  // A cell-service call that discovers a TR-1900 lockout during recovery (see
  // lib/cors-auth-recovery) defers here so an already-loaded shell switches to
  // the terminal maintenance screen at once, matching the app-shell init loop —
  // rather than showing a broken UI until the next proactive /api/init refresh.
  useEffect(() => {
    const onTenantLocked = () => handleTenantLocked();
    window.addEventListener(AUTH_TENANT_LOCKED_EVENT, onTenantLocked);
    return () => window.removeEventListener(AUTH_TENANT_LOCKED_EVENT, onTenantLocked);
  }, [handleTenantLocked]);

  // Background tabs throttle (or freeze) setTimeout, so the scheduled token
  // refresh can fire late — leaving a stale token in AuthContext / the gateway
  // cache until the next user action, which then silently fails or, on a BFF
  // call, triggers a sign-out redirect. When the tab regains focus/visibility
  // or the network comes back, re-check expiry and refresh proactively.
  useEffect(() => {
    const maybeRefreshOnReturn = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (shouldRefreshNow(decodedAccessTokenRef.current)) {
        void fetchInit();
      }
    };
    window.addEventListener('visibilitychange', maybeRefreshOnReturn);
    window.addEventListener('focus', maybeRefreshOnReturn);
    window.addEventListener('online', maybeRefreshOnReturn);
    return () => {
      window.removeEventListener('visibilitychange', maybeRefreshOnReturn);
      window.removeEventListener('focus', maybeRefreshOnReturn);
      window.removeEventListener('online', maybeRefreshOnReturn);
    };
  }, [fetchInit]);

  // Recovery poll for the TR-1900 maintenance screen. Only runs while locked:
  // re-checks /api/init every TENANT_LOCKED_POLL_MS (plus on tab focus / network
  // return), and fetchInit reloads the app the moment /api/init authenticates
  // again after an operator unlock. Distinct from the proactive token-refresh
  // loop above, which is paused while the shell is unmounted behind this screen.
  useEffect(() => {
    if (!tenantLocked) return;
    const poll = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void fetchInit();
    };
    const interval = setInterval(poll, TENANT_LOCKED_POLL_MS);
    window.addEventListener('focus', poll);
    window.addEventListener('online', poll);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', poll);
      window.removeEventListener('online', poll);
    };
  }, [tenantLocked, fetchInit]);

  if (tenantLocked) {
    // TR-1900 incident-containment screen. Deliberately neutral "maintenance"
    // copy, indistinguishable from planned downtime — we do NOT reveal that the
    // tenant has been locked (a potentially-compromised tenant must not learn
    // they've been detected). Terminal: no redirect, no sign-out link. The
    // recovery poll auto-reloads into the app once ops unlocks the tenant.
    return (
      <TimePilotLocaleProvider>
        <MaintenanceScreen />
      </TimePilotLocaleProvider>
    );
  }

  if (wrongCell) {
    return (
      <TimePilotLocaleProvider>
        <AccessDeniedScreen />
      </TimePilotLocaleProvider>
    );
  }

  return (
    <>
      {!appStartupReady && (
        <TimePilotLocaleProvider syncHtmlLang={false}>
          <TimePilotLoadingScreen />
        </TimePilotLocaleProvider>
      )}
      {features && features.ldClientSideId && (
        <div
          hidden={!appStartupReady}
          className={appStartupReady ? 'contents' : undefined}
        >
          <QueryClientProvider client={queryClient}>
            <MarkdownEditorScrollbarGuard />
            <LDProvider
              clientSideID={features.ldClientSideId as string}
              options={{ streaming: true }}
              reactOptions={{ sendEventsOnFlagRead: false }}
              timeout={LD_INIT_TIMEOUT_SECONDS}
            >
              {/* SessionProvider + IdleTimeoutManager moved to the root layout
                  (components/session/SessionMount) so idle handling covers every
                  authenticated route, not just this one — TR-3393. */}
              <Main
                features={features}
                onStartupReady={handleAppStartupReady}
              />
            </LDProvider>
          </QueryClientProvider>
        </div>
      )}
    </>
  );
};

export default Home;
